import { useState } from 'react'
import { useAuth } from '../lib/AuthContext'
import { useProfile } from '../lib/hooks'
import { generateAndSaveAiPlan } from '../lib/aiPlan'

// One question (or tightly related pair) per screen, shown after signup once
// there's a signed-in user but no profile doc yet - see Health.jsx's
// AppShell. Only the name is required to finish; everything else can be
// skipped and filled in later from Settings.
const STEP_COUNT = 9

function ProgressBar({ step }) {
  return (
    <div className="onboarding-progress">
      <div className="onboarding-progress-fill" style={{ width: `${((step + 1) / STEP_COUNT) * 100}%` }} />
    </div>
  )
}

export default function Onboarding() {
  const { user } = useAuth()
  const { updateProfile } = useProfile()
  const [step, setStep] = useState(0)
  const [busy, setBusy] = useState(false)
  const [busyStep, setBusyStep] = useState('')

  const [name, setName] = useState('')
  const [age, setAge] = useState('')
  const [gender, setGender] = useState('')
  const [heightCm, setHeightCm] = useState('')
  const [weightKg, setWeightKg] = useState('')
  const [targetWeightKg, setTargetWeightKg] = useState('')
  const [goal, setGoal] = useState('')
  const [experience, setExperience] = useState('')
  const [goesToGym, setGoesToGym] = useState('')
  const [equipment, setEquipment] = useState('')
  const [dietaryPreferences, setDietaryPreferences] = useState('')
  const [injuries, setInjuries] = useState('')

  const isLastStep = step === STEP_COUNT - 1
  const canAdvance = step !== 0 || name.trim().length > 0

  const finish = async () => {
    setBusy(true)
    setBusyStep('Saving your profile…')
    const profile = { name: name.trim(), onboardingComplete: true }
    const ageNum = parseInt(age, 10)
    const heightNum = parseFloat(heightCm)
    const weightNum = parseFloat(weightKg)
    const targetNum = parseFloat(targetWeightKg)
    if (Number.isFinite(ageNum)) profile.age = ageNum
    if (gender) profile.gender = gender
    if (Number.isFinite(heightNum)) profile.heightCm = heightNum
    if (Number.isFinite(weightNum)) profile.weightKg = weightNum
    if (Number.isFinite(targetNum)) profile.targetWeightKg = targetNum
    if (goal) profile.goal = goal
    if (experience) profile.experience = experience
    if (goesToGym) profile.goesToGym = goesToGym === 'yes'
    if (goesToGym === 'no' && equipment.trim()) profile.equipment = equipment.trim()
    if (dietaryPreferences.trim()) profile.dietaryPreferences = dietaryPreferences.trim()
    if (injuries.trim()) profile.injuries = injuries.trim()

    await updateProfile(profile)

    // Generate a personalized 90-day AI plan when we have enough to work
    // with. Best-effort: a failure here should never strand the user on the
    // wizard - if it doesn't work out, AppShell's PlanNotReady screen picks
    // up from there and lets them retry from Settings (there is no shared
    // default plan to silently fall back to).
    if (Number.isFinite(ageNum) && Number.isFinite(heightNum) && Number.isFinite(weightNum)) {
      setBusyStep('Generating your personalized 90-day plan…')
      try {
        await generateAndSaveAiPlan(user.uid, profile)
      } catch {
        // Swallow - PlanNotReady handles it from here.
      }
    }
    // AppShell re-renders into the main app (or PlanNotReady) as soon as
    // profile.onboardingComplete is true.
  }

  const goNext = (e) => {
    e?.preventDefault()
    if (!canAdvance || busy) return
    if (isLastStep) {
      finish()
      return
    }
    setStep((s) => s + 1)
  }

  const goBack = () => {
    if (busy) return
    setStep((s) => Math.max(0, s - 1))
  }

  let heading = ''
  let subtext = ''
  let body = null

  if (step === 0) {
    heading = "What should we call you?"
    subtext = "Let's set up your profile - just a few quick questions, one at a time."
    body = (
      <label>
        Your name
        <input autoFocus type="text" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} required />
      </label>
    )
  } else if (step === 1) {
    heading = `Nice to meet you, ${name.trim() || 'there'}.`
    subtext = 'A couple of basics for your plan.'
    body = (
      <div className="login-field-row">
        <label>
          Age
          <input type="number" inputMode="numeric" min="1" max="120" value={age} onChange={(e) => setAge(e.target.value)} />
        </label>
        <label>
          Gender
          <select value={gender} onChange={(e) => setGender(e.target.value)}>
            <option value="">Skip</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
        </label>
      </div>
    )
  } else if (step === 2) {
    heading = 'Your current stats'
    subtext = 'Used to estimate your maintenance calories and daily macro targets.'
    body = (
      <div className="login-field-row">
        <label>
          Height (cm)
          <input type="number" inputMode="decimal" value={heightCm} onChange={(e) => setHeightCm(e.target.value)} />
        </label>
        <label>
          Weight (kg)
          <input type="number" inputMode="decimal" value={weightKg} onChange={(e) => setWeightKg(e.target.value)} />
        </label>
      </div>
    )
  } else if (step === 3) {
    heading = 'What are you looking for?'
    subtext = "Your goal shapes how your AI plan balances training and calories."
    body = (
      <label>
        Goal
        <select autoFocus value={goal} onChange={(e) => setGoal(e.target.value)}>
          <option value="">Skip</option>
          <option value="fatLoss">Fat loss</option>
          <option value="muscleGain">Muscle gain</option>
          <option value="transformation">Full transformation</option>
          <option value="maintain">Maintain current fitness</option>
        </select>
      </label>
    )
  } else if (step === 4) {
    heading = "What's your goal weight?"
    subtext = 'This sets the pace of your fat-loss or muscle-gain target - change it any time from Settings.'
    body = (
      <label>
        Target weight (kg)
        <input type="number" inputMode="decimal" value={targetWeightKg} onChange={(e) => setTargetWeightKg(e.target.value)} />
      </label>
    )
  } else if (step === 5) {
    heading = 'Your training experience'
    subtext = 'Helps your AI plan pick the right exercises and starting weights.'
    body = (
      <label>
        Experience level
        <select value={experience} onChange={(e) => setExperience(e.target.value)}>
          <option value="">Skip</option>
          <option value="beginner">Beginner</option>
          <option value="intermediate">Intermediate</option>
          <option value="advanced">Advanced</option>
        </select>
      </label>
    )
  } else if (step === 6) {
    heading = 'Where do you train?'
    subtext = 'Gym access changes what equipment your plan assumes you have.'
    body = (
      <>
        <label>
          Gym access?
          <select value={goesToGym} onChange={(e) => setGoesToGym(e.target.value)}>
            <option value="">Skip</option>
            <option value="yes">Yes, I go to a gym</option>
            <option value="no">No, home only</option>
          </select>
        </label>
        {goesToGym === 'no' && (
          <label>
            Equipment you have at home
            <input type="text" placeholder="e.g. dumbbells, resistance bands (leave blank for bodyweight only)" value={equipment} onChange={(e) => setEquipment(e.target.value)} />
          </label>
        )}
      </>
    )
  } else if (step === 7) {
    heading = 'Any dietary preferences?'
    subtext = 'Vegetarian, allergies, anything your meal plan should work around.'
    body = (
      <label>
        Dietary preferences / restrictions
        <input autoFocus type="text" placeholder="e.g. vegetarian, no nuts" value={dietaryPreferences} onChange={(e) => setDietaryPreferences(e.target.value)} />
      </label>
    )
  } else if (step === 8) {
    heading = 'Any injuries or limitations?'
    subtext = "So your workouts avoid movements that don't work for you."
    body = (
      <label>
        Injuries / limitations
        <input autoFocus type="text" placeholder="e.g. lower back issue, avoid overhead pressing" value={injuries} onChange={(e) => setInjuries(e.target.value)} />
      </label>
    )
  }

  return (
    <div className="login-page">
      <div className="login-brand-panel">
        <img src="/health-icons/icon-512.png" alt="" className="login-brand-mark" />
        <h2 className="login-brand-title">HealthNest</h2>
        <p className="login-brand-tagline">Your 90-day plan, tracked in full - workouts, meals, macros, and progress, all in one place.</p>
        <ul className="login-brand-features">
          <li>Full macro + micronutrient tracking</li>
          <li>Workout logging with personal records</li>
          <li>Pantry &amp; shopping list management</li>
          <li>Progress photos and weekly reviews</li>
        </ul>
      </div>
      <div className="login-form-side">
        <div className="login-card">
          <ProgressBar step={step} />
          <p className="onboarding-step-label">STEP {step + 1} OF {STEP_COUNT}</p>
          <h1>{heading}</h1>
          <p className="muted">{subtext}</p>
          <form onSubmit={goNext}>
            {body}
            <div className="onboarding-nav">
              {step > 0 && (
                <button type="button" className="btn onboarding-back" onClick={goBack} disabled={busy}>
                  Back
                </button>
              )}
              <button className="btn btn-primary onboarding-next" type="submit" disabled={!canAdvance || busy}>
                {busy ? busyStep || 'Saving…' : isLastStep ? 'Finish' : 'Next'}
              </button>
            </div>
            {step > 0 && !isLastStep && (
              <button type="button" className="login-link onboarding-skip" onClick={goNext} disabled={busy}>
                Skip this question
              </button>
            )}
          </form>
        </div>
      </div>
    </div>
  )
}
